import React, { useMemo } from "react"

import { AvailablePlanType } from "@common-shared/config/coverage.config"
import { CoverageBreakdownSection } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types"
import {
    IOVCoverageResultDemographics,
    IOneVerifyCoverageCheckHistory,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../config/coverage.config"
import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { CoverageBreakdownBadgeElement } from "../../../elements/coverageBadges/coverageBreakdownBadge.element"
import { ExportReportHelper } from "../../../helpers/exportReport.helper"
import { PoliciesHelper } from "../../../helpers/policies.helper"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import type { ICoverageBreakdownData } from "../../../types/data.types"
import type { ICoverageResultBreakdownDetailsComponentProps } from "../../../types/props.types"
import { NetworkStatusAlertComponent } from "../../networkStatus/alert/networkStatusAlert.component"
import { CoverageResultFlagsComponent } from "../../policies/flags/coverageResultFlags.component"
import { CoverageResultBreakdownDetailsSectionComponent } from "./coverageResultBreakdownDetailsSection.component"

export const CoverageResultBreakdownDetailsComponent: React.FunctionComponent<
    ICoverageResultBreakdownDetailsComponentProps
> = props => {
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    // Difference between demographics that was provided by user and that insurer has on file
    const demographicsDifference = useMemo<
        Partial<IOVCoverageResultDemographics>
    >(() => {
        return PoliciesHelper.getDemographicsDifference(
            props.requestData,
            props.coverageResult.demographics,
        )
    }, [props.requestData, props.coverageResult])

    const isMedicaidPolicy = useMemo<boolean>(() => {
        return (
            !!props.coverageResult.planType &&
            COVERAGE_CONFIG.medicaidPlanTypes.includes(
                props.coverageResult.planType,
            )
        )
    }, [props.coverageResult.planType])

    const isMedicareAdvantage = useMemo<boolean>(() => {
        return (
            props.coverageResult.planType ===
            AvailablePlanType.MEDICARE_ADVANTAGE
        )
    }, [props.coverageResult.planType])

    const hasPrimaryCarePhysicianData = useMemo<boolean>(() => {
        const primaryCarePhysician = props.coverageResult.primaryCarePhysician

        if (!primaryCarePhysician) {
            return false
        }

        return !!(
            primaryCarePhysician.name ||
            primaryCarePhysician.npi ||
            primaryCarePhysician.organization
        )
    }, [props.coverageResult.primaryCarePhysician])

    const generatedCoverageCheckHistory =
        useMemo<IOneVerifyCoverageCheckHistory>(() => {
            return {
                requestData: props.requestData,
                checkSpecificRequestData: {},
                coverageResult: {
                    ...props.coverageResult,

                    // Use latest override that was fetched separately
                    override:
                        props.coverageOverride || props.coverageResult.override,
                },
                policy: props.policy,
                responsePayer: props.responsePayer,
                requestPayer: props.requestPayer,
                requestedHealthProvider: props.requestedHealthProvider,
                agenticCheck: props.agenticCheck,
                preAgenticCoverageResult: props.preAgenticCoverageResult,
            }
        }, [
            props.requestData,
            props.coverageResult,
            props.coverageOverride,
            props.policy,
            props.responsePayer,
            props.requestPayer,
            props.requestedHealthProvider,
            props.agenticCheck,
            props.preAgenticCoverageResult,
        ])

    const convertPolicyColumnToBreakdownData = (
        columnConfig: IPolicyColumnConfiguration,
    ): ICoverageBreakdownData => {
        const value = ExportReportHelper.getMappedColumnValue(
            generatedCoverageCheckHistory,
            // Column's own emptyColumnValue wins over the fallback argument, so override it for the breakdown
            columnConfig.coverageBreakdownEmptyValue
                ? {
                      ...columnConfig,

                      emptyColumnValue:
                          columnConfig.coverageBreakdownEmptyValue,
                  }
                : columnConfig,
            COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA,
        )

        const result: ICoverageBreakdownData = {
            label:
                columnConfig.coverageBreakdownLabelOverride ||
                columnConfig.label,
            value,
            fieldKey: columnConfig.columnKey,
            tooltip:
                value === COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                    ? columnConfig.coverageBreakdownEmptyValueTooltip
                    : value ===
                        COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown
                      ? columnConfig.coverageBreakdownUnknownValueTooltip
                      : columnConfig.coverageBreakdownTooltip,
            isHighlighted: PoliciesHelper.isColumnCellHighlighted(
                generatedCoverageCheckHistory,
                columnConfig,
            ),
        }

        if (!result.isHighlighted) {
            const flaggedField = PoliciesHelper.isColumnCellFlagged(
                generatedCoverageCheckHistory,
                columnConfig,
            )

            if (flaggedField) {
                result.flaggedField = flaggedField
            }
        }

        return result
    }

    const getPlanStatusBreakdownData = (): ICoverageBreakdownData => {
        const data = convertPolicyColumnToBreakdownData(
            POLICIES_COLUMNS_CONFIG.columnsConfig.PLAN_STATUS,
        )

        if (typeof data.value === "string") {
            data.value = (
                <CoverageBreakdownBadgeElement
                    label={data.value}
                    isActive={
                        !!props.coverageResult.planStatus &&
                        CHECKER_CONFIG.activePlanStatuses.includes(
                            props.coverageResult.planStatus,
                        )
                    }
                    tooltip={data.tooltip}
                    labelTypography="captionSemibold"
                />
            )

            data.tooltip = undefined
        }

        return data
    }

    const getCoverageStatusBreakdownData = (): ICoverageBreakdownData => {
        const data = convertPolicyColumnToBreakdownData(
            POLICIES_COLUMNS_CONFIG.columnsConfig.COVERAGE_STATUS,
        )

        if (typeof data.value === "string") {
            data.value = (
                <CoverageBreakdownBadgeElement
                    label={data.value}
                    isActive={
                        !!props.coverageResult.modalityCoverageStatusTernary
                    }
                    tooltip={data.tooltip}
                    labelTypography="captionSemibold"
                />
            )

            data.tooltip = undefined
        }

        return data
    }

    const sectionsData = useMemo<
        Record<CoverageBreakdownSection, ICoverageBreakdownData[]>
    >(() => {
        const result: Record<
            CoverageBreakdownSection,
            ICoverageBreakdownData[]
        > = {
            [CoverageBreakdownSection.FLAGS]:
                props.withFlagsSection && props.coverageResult.flags.length
                    ? [
                          {
                              value: (
                                  <CoverageResultFlagsComponent
                                      flags={props.coverageResult.flags}
                                  />
                              ),
                          },
                      ]
                    : [],
            [CoverageBreakdownSection.POLICY_GENERAL]: [
                // Don't show payer value difference if in error mode and response payer is not defined
                {
                    label: "Payer",
                    value: props.requestPayer
                        ? [
                              props.requestPayer.insuranceName,
                              props.requestPayer.payerId,
                          ]
                              .filter(item => !!item)
                              .join(" / ")
                        : COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
                    valueDifference: props.responsePayer
                        ? [
                              props.responsePayer.insuranceName,
                              props.responsePayer.payerId,
                          ]
                              .filter(item => !!item)
                              .join(" / ")
                        : !props.isErrorModeFields
                          ? COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown
                          : undefined,
                    fieldKey: PolicyColumnKey.IDENTIFIED_PAYER,
                },
                {
                    label: "Member ID",
                    value:
                        props.requestData.memberId ||
                        props.coverageResult.demographics?.memberId ||
                        COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown,
                    valueDifference:
                        demographicsDifference.memberId ?? undefined,
                    fieldKey: PolicyColumnKey.MEMBER_ID,
                },

                ...(!props.isErrorModeFields
                    ? [
                          getPlanStatusBreakdownData(),
                          getCoverageStatusBreakdownData(),
                      ]
                    : []),
            ],
            [CoverageBreakdownSection.POLICY_THIRD_PARTY]: [],
            [CoverageBreakdownSection.POLICY_MEDICARE_ADVANTAGE]: [],
            [CoverageBreakdownSection.POLICY_ADDITIONAL_POLICY]: [],
            [CoverageBreakdownSection.POLICY_FINANCIALS]: [
                // Need this placeholder so deductible is pulled to the right
                // Since process for breaking in columns is automatic: it finds middle of array
                {
                    label: COVERAGE_CONFIG.breakdownPlaceholderLabel,
                    value: null,
                },
            ],
            [CoverageBreakdownSection.POLICY_OBLIGATION]: [],
            [CoverageBreakdownSection.SPECIALTY_SPECIFIC]: [],
            [CoverageBreakdownSection.TELEHEALTH]: [],
            [CoverageBreakdownSection.PRIMARY_CARE_PHYSICIAN]: [],
        }

        const modalitySpecificColumns = selectedPracticeRole
            ?.availableModalities?.length
            ? ExportReportHelper.getModalitiesSpecificColumnsConfigurations(
                  selectedPracticeRole.availableModalities,
              )
            : []

        Object.values(CoverageBreakdownSection).forEach(section => {
            let neededColumns = [
                // Remove modality specific columns definitions, they will be added below based on modality
                ...Object.values(POLICIES_COLUMNS_CONFIG.columnsConfig).filter(
                    item => !item.isModalitySpecific,
                ),

                ...modalitySpecificColumns,
            ].filter(column => column.coverageBreakdownSection === section)

            // Don't show POLICY_THIRD_PARTY columns if it's a Medicare Advantage policy
            // (we show POLICY_MEDICARE_ADVANTAGE instead)
            if (
                section === CoverageBreakdownSection.POLICY_THIRD_PARTY &&
                isMedicareAdvantage
            ) {
                neededColumns = []
            }

            // Only show POLICY_MEDICARE_ADVANTAGE columns if it's a Medicare Advantage policy
            if (
                section ===
                    CoverageBreakdownSection.POLICY_MEDICARE_ADVANTAGE &&
                (!isMedicareAdvantage ||
                    !props.coverageResult.medicareAdvantage)
            ) {
                neededColumns = []
            }

            // Only show PRIMARY_CARE_PHYSICIAN columns if there is primary care physician data
            if (
                section === CoverageBreakdownSection.PRIMARY_CARE_PHYSICIAN &&
                !hasPrimaryCarePhysicianData
            ) {
                neededColumns = []
            }

            // Show different columns for Medicaid policies
            neededColumns = isMedicaidPolicy
                ? neededColumns.filter(
                      item =>
                          item.isMedicaidSpecific || item.isMedicaidApplicable,
                  )
                : neededColumns.filter(item => !item.isMedicaidSpecific)

            if (props.isErrorModeFields) {
                neededColumns = neededColumns.filter(
                    item => item.showCoverageBreakdownErrorMode,
                )
            }

            // Add common additional columns from common columns config
            for (const column of neededColumns) {
                const breakdownData = convertPolicyColumnToBreakdownData(column)
                const target = result[section]

                // If order provided - put at that order
                typeof column.coverageBreakdownOrder === "undefined"
                    ? target.push(breakdownData)
                    : target.splice(
                          column.coverageBreakdownOrder,
                          0,
                          breakdownData,
                      )
            }
        })

        return result
    }, [
        props.coverageResult,
        props.requestData,
        props.coverageOverride,
        selectedPracticeRole?.availableModalities,
        generatedCoverageCheckHistory,
        demographicsDifference,
        isMedicaidPolicy,
        isMedicareAdvantage,
        hasPrimaryCarePhysicianData,
    ])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <NetworkStatusAlertComponent
                className="my-[18px]"
                networkStatusAlert={props.coverageResult.networkStatusAlert}
                isInNetworkCheck={props.coverageResult.isInNetworkCheck}
                bluecardEligible={props.coverageResult.bluecardEligible}
                requestPayer={props.requestPayer}
                responsePayer={props.responsePayer}
            />

            <div
                key={props.overridesFormKey}
                className="grid grid-cols-2 gap-x-8 mt-[16px]"
            >
                {(Object.keys(sectionsData) as CoverageBreakdownSection[]).map(
                    (sectionKey, index) => {
                        if (
                            !sectionsData[sectionKey].filter(
                                item =>
                                    item.label !==
                                    COVERAGE_CONFIG.breakdownPlaceholderLabel,
                            ).length
                        ) {
                            return null
                        }

                        const sectionConfig =
                            COVERAGE_CONFIG.coverageBreakdownSections[
                                sectionKey
                            ]

                        return (
                            <CoverageResultBreakdownDetailsSectionComponent
                                key={index}
                                className={`
                                    ${
                                        !sectionConfig.singleColumn
                                            ? "col-span-2"
                                            : "md:col-span-2"
                                    }
                                    ${sectionConfig.bigFont ? "mt-[8px]" : ""}
                                `}
                                sectionKey={sectionKey}
                                coverageCheckHistory={
                                    generatedCoverageCheckHistory
                                }
                                data={sectionsData[sectionKey]}
                                isOverridesModeActivated={
                                    props.isOverridesModeActivated
                                }
                                isOverridesFormDisabled={
                                    props.isOverridesFormDisabled
                                }
                            />
                        )
                    },
                )}
            </div>
        </div>
    )
}
